<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue';
import { animate, stagger } from 'animejs';
import StartButton from './button/StartButton.vue';
import { ChevronUp, Pointer } from 'lucide-vue-next';

const scrollY = ref(0);
const handleScroll = () => {
  scrollY.value = window.scrollY;
};

onMounted(() => {
  animate('.animate-in', {
    translateY: [30, 0],
    opacity: [0, 1],
    delay: stagger(200),
    duration: 1200,
    easing: 'easeOutExpo'
  });

  window.addEventListener('scroll', handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const scrollIndicatorStyle = computed(() => {
  const threshold = 400; // Distance over which the effect completes
  const progress = Math.min(scrollY.value / threshold, 1);
  
  return {
    opacity: 0.5 * (1 - progress),
    transform: `translateX(-50%) translateY(-${progress * 200}px)`
  };
});
</script>

<template>
  <section class="hero">
    <div class="bg-blob blob-1"></div>
    <div class="bg-blob blob-2"></div>
    
    <div class="hero-content">
      <div class="badge animate-in">
        <img src="@/assets/icon-brand.svg" alt="betPic Icon" class="badge-icon" />
        SHARE <span class="badge-brand">BET PICS</span> WITH A CALL TO ACTION
      </div>
      
      <h1 class="animate-in">
        Social betting <br />
        <span class="text-gradient">begins in a chat</span>
      </h1>
      
      <p class="animate-in hero-sub">
        ... it might end <span class="text-accent">in your sportsbook</span>
      </p>

      <p class="animate-in hero-desc">
        The bet is discussed socially.
        By the time it is decided it could go anywhere.
        Increase the chances of it being your sportsbook.
      </p>
      
      <div class="hero-actions animate-in">
        <StartButton />
      </div>
    </div>
    
    <div class="scroll-indicator" :style="scrollIndicatorStyle">
      <div class="mouse-icon">
        <div class="mouse">
          <div class="wheel"></div>
        </div>
      </div>
      <div class="swipe-icon">
        <div class="swipe-container">
          <ChevronUp class="swipe-arrow" />
          <div class="ripple"></div>
          <Pointer class="hand-icon" />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
  padding: 80px 5%;
}

.bg-blob {
  position: absolute;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, var(--accent-glow) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(80px);
  z-index: -2;
  opacity: 0.2;
}

.blob-1 { top: -10%; left: -10%; }
.blob-2 { bottom: -10%; right: -10%; background: radial-gradient(circle, var(--red-depth) 0%, transparent 70%); }

.hero-content {
  max-width: 900px;
  text-align: center;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(230, 0, 0, 0.1);
  border: 1px solid rgba(230, 0, 0, 0.3);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--white-motion);
  margin-bottom: 24px;
}

.badge-icon {
  width: 16px;
  height: 16px;
}

.badge-brand {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--red-primary);
}

h1 {
  font-size: 5rem;
  font-weight: 800;
  line-height: 1.05;
  margin-bottom: 24px;
  letter-spacing: -3px;
  color: var(--text-primary);
}

.hero-sub {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 16px;
  line-height: 1.2;
}

.text-accent {
  color: var(--red-primary);
}

.hero-desc {
  font-size: 1.2rem;
  color: var(--text-secondary);
  margin-bottom: 32px;
  max-width: 600px;
  line-height: 1.6;
}

.scroll-indicator {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0.5;
  will-change: transform, opacity;
  transition: opacity 0.1s ease-out; /* Slight smoothing */
}

.mouse-icon {
  display: block;
}

.swipe-icon {
  display: none;
}

.swipe-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.swipe-arrow {
  width: 24px;
  height: 24px;
  color: var(--text-secondary);
  animation: arrowUp 2s infinite ease-in-out;
}

.hand-icon {
  width: 40px;
  height: 40px;
  color: var(--text-secondary);
  position: relative;
  z-index: 2;
  margin-top: -4px; /* Adjust to align with arrow and ripple */
}

.ripple {
  position: absolute;
  top: 4px; /* Positioned at the fingertip */
  width: 24px;
  height: 24px;
  border: 1.5px solid var(--text-secondary);
  border-radius: 50%;
  opacity: 0;
  animation: rippleEffect 2s infinite ease-in-out;
}

.mouse {
  width: 24px; height: 40px;
  border: 2px solid var(--text-secondary);
  border-radius: 12px;
  display: flex; justify-content: center; padding-top: 8px;
}

.wheel {
  width: 4px; height: 8px;
  background: var(--text-secondary);
  border-radius: 2px;
  animation: scroll 1.5s infinite;
}

@keyframes scroll {
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(15px); opacity: 0; }
}

@keyframes arrowUp {
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50% { transform: translateY(-10px); opacity: 1; }
}

@keyframes rippleEffect {
  0% { transform: scale(0.5); opacity: 0.8; }
  100% { transform: scale(2); opacity: 0; }
}

@media (max-width: 768px) {
  .hero { padding: 40px 5%; min-height: 100dvh; } /* Changed to 100dvh to fill screen exactly */
  h1 { font-size: 2.8rem; letter-spacing: -1px; }
  .hero-sub { font-size: 1.5rem; }
  .hero-desc { font-size: 1rem; margin-bottom: 24px; }
  .scroll-indicator { bottom: 15px; }
  
  .mouse-icon { display: none; }
  .swipe-icon { display: block; }
}
</style>
